import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Copy, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { fetchMeta } from "@/lib/api";
import { Reveal, SplitLines } from "@/components/Reveal";

export const Calculator = () => {
  const { data: meta } = useQuery({ queryKey: ["meta"], queryFn: fetchMeta });
  const sizes = meta?.sizes || [];
  const [area, setArea] = useState(50);
  const [unit, setUnit] = useState("sqm");
  const [sizeSlug, setSizeSlug] = useState("600-x-1200-mm");
  const [wastage, setWastage] = useState(10);

  const res = useMemo(() => {
    const s = sizes.find((x) => x.slug === sizeSlug);
    if (!s || !area) return null;
    const sqm = unit === "sqm" ? Number(area) : Number(area) * 0.092903;
    const total = sqm * (1 + wastage / 100);
    const tileArea = (s.w * s.h) / 1_000_000;
    const tiles = Math.ceil(total / tileArea);
    const boxes = Math.ceil(tiles / s.pcs_per_box);
    return { sqm, total, tiles, boxes, s, boxSqm: tileArea * s.pcs_per_box };
  }, [area, unit, sizeSlug, wastage, sizes]);

  const summary = res ? `Tile enquiry — ${res.s.label}: ${res.total.toFixed(1)} m² incl. ${wastage}% wastage → ${res.tiles} tiles / ${res.boxes} boxes` : "";

  return (
    <section id="calculator" data-testid="calculator-section" className="bg-[var(--bg)] py-28 lg:py-36">
      <div className="container-x grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Reveal><p className="eyebrow mb-6">Tiles Calculator</p></Reveal>
          <SplitLines as="h2" inView className="font-display text-3xl sm:text-4xl lg:text-5xl font-light leading-[1.05] tracking-tight text-[var(--navy)]" lines={["Know exactly", <>how much <em className="italic text-[var(--mint)]">to order.</em></>]} />
          <Reveal delay={0.3}><p className="mt-8 max-w-md text-base leading-relaxed text-[var(--muted)]">Enter your floor or wall area, pick a format and we will compute tiles and boxes including a wastage allowance for cuts.</p></Reveal>
        </div>
        <Reveal delay={0.2} className="lg:col-span-7 glass rounded-[1.5rem] p-6 sm:p-10">
          <div className="grid gap-6 sm:grid-cols-2">
            <label className="block">
              <span className="mb-2 block font-mono text-[0.6rem] uppercase tracking-[0.25em] text-[var(--muted)]">Area</span>
              <div className="flex gap-2">
                <input data-testid="calc-area-input" type="number" min="1" value={area} onChange={(e) => setArea(e.target.value)} className="field" />
                <div className="flex rounded-xl border border-slate-200 bg-white p-1">
                  {["sqm", "sqft"].map((u) => <button key={u} type="button" data-testid={`calc-unit-${u}`} onClick={() => setUnit(u)} className={`rounded-lg px-3 text-xs font-semibold transition-colors ${unit === u ? "bg-[var(--navy)] text-white" : "text-[var(--navy)]"}`}>{u === "sqm" ? "m²" : "ft²"}</button>)}
                </div>
              </div>
            </label>
            <label className="block">
              <span className="mb-2 block font-mono text-[0.6rem] uppercase tracking-[0.25em] text-[var(--muted)]">Tile size</span>
              <select data-testid="calc-size-select" value={sizeSlug} onChange={(e) => setSizeSlug(e.target.value)} className="field">
                {sizes.map((s) => <option key={s.slug} value={s.slug}>{s.label}</option>)}
              </select>
            </label>
            <label className="block sm:col-span-2">
              <span className="mb-2 flex justify-between font-mono text-[0.6rem] uppercase tracking-[0.25em] text-[var(--muted)]"><span>Wastage allowance</span><span className="text-[var(--mint)]">{wastage}%</span></span>
              <input data-testid="calc-wastage-range" type="range" min="0" max="20" value={wastage} onChange={(e) => setWastage(Number(e.target.value))} className="w-full accent-[var(--mint)]" />
            </label>
          </div>
          {res && (
            <div className="mt-8 grid grid-cols-3 divide-x divide-slate-200 rounded-2xl bg-[var(--navy)] text-white" data-testid="calc-results">
              {[["Total area", `${res.total.toFixed(1)} m²`, "calc-result-area"], ["Tiles", res.tiles.toLocaleString(), "calc-result-tiles"], ["Boxes", res.boxes.toLocaleString(), "calc-result-boxes"]].map(([k, v, id]) => (
                <div key={k} className="p-5 sm:p-6">
                  <div className="font-mono text-[0.55rem] uppercase tracking-[0.25em] text-slate-300">{k}</div>
                  <div className="mt-2 font-display text-2xl sm:text-4xl font-light" data-testid={id}>{v}</div>
                </div>
              ))}
            </div>
          )}
          {res && <p className="mt-3 text-xs text-[var(--muted)]">{res.s.pcs_per_box} pcs / box · {res.boxSqm.toFixed(2)} m² per box · {res.s.thickness} mm thickness</p>}
          <div className="mt-6 flex flex-wrap gap-3">
            <button type="button" data-testid="calc-copy-btn" onClick={() => { navigator.clipboard?.writeText(summary); toast.success("Summary copied to clipboard"); }} className="btn-pill btn-ghost text-xs"><Copy size={14} /> Copy summary</button>
            <Link to="/#contact" state={{ quantity: res?.total?.toFixed(1), message: summary }} data-testid="calc-quote-btn" className="btn-pill btn-mint text-xs">Request quotation <ArrowUpRight size={14} className="arrow" /></Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
